import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { auth } from "../../firebase";
import { onAuthStateChanged } from "firebase/auth";
import { httpsCallable, getFunctions } from "firebase/functions";
import { Icons } from "../../lib/Icons";

const SubscriptionSuccessPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (!currentUser) {
        navigate("/signup/solo");
        return;
      }

      // Verify subscription status
      try {
        const functions = getFunctions(undefined, 'europe-west3');
        const checkSubscription = httpsCallable(functions, 'checkSoloSubscriptionFunc');
        
        // May need to wait a moment for webhook to process
        let attempts = 0;
        const maxAttempts = 5;
        
        while (attempts < maxAttempts) {
          const result = await checkSubscription();
          
          if (result.data.hasActiveSubscription) {
            // Force token refresh to get new claims
            await currentUser.getIdToken(true);
            setLoading(false);
            break;
          }
          
          attempts++;
          if (attempts < maxAttempts) {
            // Wait 2 seconds before retrying
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
        
        setLoading(false);
      } catch (e) {
        console.error('Error verifying subscription:', e);
        setError('Unable to verify subscription status. Please try refreshing the page.');
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate, searchParams]);

  const handleGoToDashboard = () => {
    navigate("/solo/dashboard");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-white/20 border-t-white mx-auto mb-4"></div>
          <p className="text-white text-lg">Setting up your account...</p>
          <p className="text-purple-200 text-sm mt-2">This will only take a moment</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800">
      {/* Decorative star background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(15)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-pulse"
            style={{
              left: `${10 + (i * 6) % 80}%`,
              top: `${5 + (i * 7) % 90}%`,
              animationDelay: `${i * 0.2}s`,
              animationDuration: `${2 + (i % 3)}s`,
            }}
          >
            <svg className="w-4 h-4 text-white/20" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </div>
        ))}
      </div>

      <div className="relative flex min-h-screen flex-col justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-lg text-center">
          {/* Success icon */}
          <div className="mx-auto w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mb-6 border-4 border-green-500/30">
            <svg className="w-12 h-12 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          {/* Success message */}
          <h1 className="text-4xl font-bold text-white mb-4">
            Welcome Aboard!
          </h1>
          <p className="text-xl text-purple-200 mb-8">
            Your free trial has started. Let's find some amazing resources!
          </p>

          {error && (
            <div className="mb-6 p-4 bg-yellow-500/20 rounded-xl border border-yellow-400/30 max-w-md mx-auto">
              <p className="text-yellow-100 text-sm">{error}</p>
            </div>
          )}

          {/* Trial info card */}
          <div className="bg-white/10 backdrop-blur-lg rounded-2xl border border-white/20 p-6 mb-8 max-w-md mx-auto">
            <div className="flex items-center gap-4 text-left">
              <div className="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Icons.Calendar className="w-6 h-6 text-purple-300" />
              </div>
              <div>
                <p className="text-white font-semibold">30-Day Free Trial</p>
                <p className="text-purple-200 text-sm">
                  Your trial is active. No charge until it ends.
                </p>
              </div>
            </div>
          </div>

          {/* What's next section */}
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 mb-8 max-w-md mx-auto">
            <h3 className="text-white font-semibold mb-4">Here's what you can do now:</h3>
            <ul className="space-y-3 text-left">
              {[
                "Browse 100+ teaching activities",
                "Watch video demonstrations",
                "Search for specific resources",
                "Save your favorites",
              ].map((item, idx) => (
                <li key={idx} className="flex items-center gap-3 text-purple-200">
                  <span className="flex-shrink-0 w-6 h-6 bg-purple-500/20 rounded-full flex items-center justify-center text-sm">
                    {idx + 1}
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* CTA Button */}
          <button
            onClick={handleGoToDashboard}
            className="rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 px-8 py-4 text-white font-bold text-lg shadow-lg hover:from-purple-600 hover:to-pink-600 focus:outline-none focus:ring-2 focus:ring-purple-400 transition-all"
          >
            Explore Resources →
          </button>

          {/* Email reminder */}
          <p className="mt-6 text-purple-300/70 text-sm">
            We've sent a confirmation email. We'll remind you before your trial ends.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSuccessPage;


